'use client';

import { c as _c } from "react/compiler-runtime";
import React, { memo, MouseEventHandler, useContext } from 'react';
import { FacetType, RangeFacet, ValueFacet } from '@/src/lib/category/types.ts';
import AppButton from '@/src/app/components/Button/AppButton.tsx';
import { ButtonSize } from '@/src/app/components/Button/Button.tsx';
import { getFacetType, prepareFacets } from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/lib/business/category-page-filters.ts';
import { Dictionary } from '@/src/lib/dictionary/types.ts';
import ValueFilter from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/components/Filters/ValueFilter.tsx';
import RangeFilter from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/components/Filters/RangeFilter.tsx';
import { LayoutTemplateContext } from '@/src/app/[country]/[lang]/shop/[...segments]/(categories)/LayoutTemplateProvider.tsx';
import Icon from '@/src/app/components/Icon/Icon.tsx';
import { IconName } from '@/src/app/components/Icon/types.ts';
type Props = {
  facets: Array<ValueFacet | RangeFacet>;
  dict: Dictionary;
  onCloseClick: MouseEventHandler<HTMLButtonElement> & MouseEventHandler<HTMLLinkElement>;
  numFound: number;
};
const OffcanvasFilters = memo(function OffcanvasFilters(t0) {
  const $ = _c(38);
  const {
    facets,
    dict,
    onCloseClick,
    numFound
  } = t0;
  const {
    isPending
  } = useContext(LayoutTemplateContext);
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== dict || $[1] !== facets || $[2] !== onCloseClick) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const validFacets = prepareFacets(facets);
      if (!validFacets.length) {
        t6 = undefined;
        break bb0;
      }
      t3 = "h-dvh min-h-96 flex flex-col";
      t4 = "offcanvas-filters";
      let t7;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <Icon name={IconName.CLOSE} className="text-4xl!" />;
        $[9] = t7;
      } else {
        t7 = $[9];
      }
      let t8;
      if ($[10] !== onCloseClick) {
        t8 = <button onClick={onCloseClick} data-testid="close-offcanvas-filters">{t7}</button>;
        $[10] = onCloseClick;
        $[11] = t8;
      } else {
        t8 = $[11];
      }
      let t9;
      if ($[12] !== dict.category.filters.moreFilters) {
        t9 = <span className="roofline">{dict.category.filters.moreFilters}</span>;
        $[12] = dict.category.filters.moreFilters;
        $[13] = t9;
      } else {
        t9 = $[13];
      }
      let t10;
      if ($[14] !== dict.category.filters.offCanvas.title) {
        t10 = <h1 className="display-title-3xl mt-0">{dict.category.filters.offCanvas.title}</h1>;
        $[14] = dict.category.filters.offCanvas.title;
        $[15] = t10;
      } else {
        t10 = $[15];
      }
      let t11;
      if ($[16] !== t10 || $[17] !== t9) {
        t11 = <div className="ml-5 md:ml-10">{t9}{t10}</div>;
        $[16] = t10;
        $[17] = t9;
        $[18] = t11;
      } else {
        t11 = $[18];
      }
      if ($[19] !== t11 || $[20] !== t8) {
        t5 = <header className="bg-primary px-5 md:px-12 py-3 md:py-8 flex items-start">{t8}{t11}</header>;
        $[19] = t11;
        $[20] = t8;
        $[21] = t5;
      } else {
        t5 = $[21];
      }
      t1 = "flex-1 overflow-y-auto bg-gray-100 h-full divide-gray-300 divide-y divide-solid";
      let t12;
      if ($[22] !== dict) {
        t12 = facet => <div className="px-5 md:px-12 py-5 md:py-8" key={facet.name}>{getFacetType(facet) === FacetType.VALUES ? <ValueFilter facet={facet as ValueFacet} key={facet.name} showChips={true} /> : null}{getFacetType(facet) === FacetType.RANGE ? <RangeFilter facet={facet as RangeFacet} dict={dict} key={facet.name} showChips={true} plain={true} /> : null}</div>;
        $[22] = dict;
        $[23] = t12;
      } else {
        t12 = $[23];
      }
      t2 = validFacets.map(t12);
    }
    $[0] = dict;
    $[1] = facets;
    $[2] = onCloseClick;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[24] !== t1 || $[25] !== t2) {
    t7 = <div className={t1}>{t2}</div>;
    $[24] = t1;
    $[25] = t2;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  let t8;
  if ($[27] !== dict.category.filters.offCanvas.showResults || $[28] !== isPending || $[29] !== numFound || $[30] !== onCloseClick) {
    t8 = <footer className="bg-white p-5 md:p-12 border-t border-gray-300"><AppButton onClick={onCloseClick} type="button" primary={true} priority={true} size={ButtonSize.LARGE} className="w-full" disabled={isPending}>{dict.category.filters.offCanvas.showResults} ({numFound})</AppButton></footer>;
    $[27] = dict.category.filters.offCanvas.showResults;
    $[28] = isPending;
    $[29] = numFound;
    $[30] = onCloseClick;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t3 || $[33] !== t4 || $[34] !== t5 || $[35] !== t7 || $[36] !== t8) {
    t9 = <section className={t3} data-testid={t4}>{t5}{t7}{t8}</section>;
    $[32] = t3;
    $[33] = t4;
    $[34] = t5;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  return t9;
});
export default OffcanvasFilters;